import { useId } from 'react';
import { type AeroProfitFloorDraft, createDefaultProfitFloor } from '@/features/tradeLogicAuthoring/components/aeroOptimizationJobInput';

interface ProfitFloorEditorProps {
    floors: AeroProfitFloorDraft[];
    onChange: (floors: AeroProfitFloorDraft[]) => void;
    disabled?: boolean;
}

const rowInputClass =
    'h-[34px] w-[72px] rounded-md border border-[#444] bg-[#1E1E1E] px-2 text-sm text-zinc-200 outline-none focus:border-[#888]';

const isInvertedFloor = (floor: AeroProfitFloorDraft): boolean => {
    const trigger = Number(floor.triggerPercentage);
    const sellAt = Number(floor.sellAtPercentage);

    return Number.isFinite(trigger) && Number.isFinite(sellAt) && trigger > 0 && sellAt > 0 && sellAt >= trigger;
};

const ProfitFloorEditor = ({ floors, onChange, disabled = false }: ProfitFloorEditorProps) => {
    const hasInvertedFloor = floors.some(isInvertedFloor);

    const updateFloor = (index: number, changes: Partial<AeroProfitFloorDraft>) => {
        onChange(floors.map((floor, floorIndex) => (floorIndex === index ? { ...floor, ...changes } : floor)));
    };

    const removeFloor = (index: number) => {
        onChange(floors.filter((_, floorIndex) => floorIndex !== index));
    };

    const addFloor = () => {
        onChange([...floors, createDefaultProfitFloor()]);
    };

    return (
        <div className="space-y-2">
            {floors.length === 0 && (
                <div className="rounded-md border border-dashed border-zinc-700 bg-black/20 px-3 py-2.5 text-sm text-zinc-500">
                    No profit floors yet. Add one to lock in gains after a trade runs up.
                </div>
            )}

            {floors.map((floor, index) => (
                <div
                    className="flex flex-wrap items-center gap-2 rounded-md border border-zinc-800 bg-black/20 px-3 py-2 text-sm text-zinc-300"
                    key={index}
                >
                    <ProfitFloorRowFields disabled={disabled} floor={floor} onChange={(changes) => updateFloor(index, changes)} />
                    <button
                        aria-label={`Remove profit floor ${index + 1}`}
                        className="ml-auto flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-red-300 disabled:opacity-40"
                        disabled={disabled}
                        type="button"
                        onClick={() => removeFloor(index)}
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                        </svg>
                    </button>
                </div>
            ))}

            <div className="flex flex-wrap items-center justify-between gap-2">
                <button
                    className="rounded-md border border-dashed border-zinc-600 px-3 py-1.5 text-sm font-medium text-cyan-400 hover:border-cyan-500 hover:text-cyan-300 disabled:opacity-40"
                    disabled={disabled}
                    type="button"
                    onClick={addFloor}
                >
                    + Add profit floor
                </button>
                {hasInvertedFloor && <span className="text-xs text-amber-300">A floor&apos;s exit % should be below its trigger %.</span>}
            </div>
        </div>
    );
};

const ProfitFloorRowFields = ({
    floor,
    disabled,
    onChange
}: {
    floor: AeroProfitFloorDraft;
    disabled: boolean;
    onChange: (changes: Partial<AeroProfitFloorDraft>) => void;
}) => {
    const triggerId = useId();
    const sellId = useId();

    return (
        <>
            <label className="text-zinc-500" htmlFor={triggerId}>
                If profit reaches
            </label>
            <input
                className={rowInputClass}
                disabled={disabled}
                id={triggerId}
                inputMode="decimal"
                placeholder="15"
                value={floor.triggerPercentage}
                onChange={(event) => onChange({ triggerPercentage: event.target.value })}
            />
            <span className="text-zinc-500">%, exit if it falls back to</span>
            <input
                className={rowInputClass}
                disabled={disabled}
                id={sellId}
                inputMode="decimal"
                placeholder="5"
                value={floor.sellAtPercentage}
                onChange={(event) => onChange({ sellAtPercentage: event.target.value })}
            />
            <span className="text-zinc-500">%</span>
        </>
    );
};

export default ProfitFloorEditor;
